// Voice utilities — MediaRecorder wrapper + Whisper STT + TTS playback.
// Admin-gated (backend enforces role); this module intentionally has no
// role check so the button/page components can be reused if we later
// open voice to all users.

import { API } from "@/lib/api";

// Picks the best mime type the browser supports for MediaRecorder.
// Chrome / Edge / Firefox: webm/opus. Safari: mp4 (falls back). We hand
// the raw blob to /api/stt/transcribe which forwards to Whisper.
export function pickRecorderMime() {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",
  ];
  for (const t of candidates) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return "";
}

export function isVoiceSupported() {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== "undefined"
  );
}

// Feature flag — admins are always allowed; non-admin users need an
// explicit `voice_beta: true` on their profile (flipped from the admin
// dashboard). Kept centralized so we can later add a subscription tier
// or a self-serve opt-in without hunting through the UI.
export function isVoiceEnabledFor(user) {
  if (!user || typeof user !== "object") return false;
  if (user.role === "admin") return true;
  return !!user.voice_beta;
}

// Creates a controllable recorder. Resolves with a Blob on stop().
export async function startRecording() {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });
  const mime = pickRecorderMime();
  const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
  const chunks = [];
  rec.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  };

  const stopped = new Promise((resolve) => {
    rec.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      const blob = new Blob(chunks, { type: mime || rec.mimeType || "audio/webm" });
      resolve(blob);
    };
  });

  rec.start(250); // small timeslice so chunks flush eagerly

  return {
    stream,
    recorder: rec,
    stop: async () => {
      if (rec.state !== "inactive") rec.stop();
      return stopped;
    },
    cancel: () => {
      try {
        rec.ondataavailable = null;
        if (rec.state !== "inactive") rec.stop();
      } finally {
        stream.getTracks().forEach((t) => t.stop());
      }
    },
  };
}

// POST audio to /api/stt/transcribe. Returns { text, language, engine }.
// Throws Error with a user-facing message. `.empty` is set on the error
// when the backend reported no speech in the recording so the caller
// can toast a calmer message.
export async function transcribeAudio(blob, { language } = {}) {
  if (!blob || blob.size === 0) throw new Error("Empty recording.");
  const form = new FormData();
  const ext = (blob.type || "").includes("mp4")
    ? "m4a"
    : (blob.type || "").includes("ogg")
      ? "ogg"
      : "webm";
  form.append("file", blob, `voice.${ext}`);
  if (language) form.append("language", language);

  const res = await fetch(`${API}/stt/transcribe`, {
    method: "POST",
    body: form,
    credentials: "include",
  });
  if (!res.ok) {
    let msg = `Transcription failed (${res.status})`;
    let empty = false;
    try {
      const j = await res.json();
      // FastAPI wraps our structured error under `detail`.
      if (j?.detail) {
        if (typeof j.detail === "object") {
          msg = j.detail.message || msg;
          empty = j.detail.error === "empty_audio";
        } else {
          msg = j.detail;
        }
      }
    } catch {
      /* ignore */
    }
    const err = new Error(msg);
    err.status = res.status;
    err.empty = empty;
    throw err;
  }
  return res.json();
}

// Fetch TTS audio from /api/tts (Sarvam Bulbul v3) and play through an
// audio element. Returns a Promise that resolves when playback ends and
// exposes:
//   - `abort()` to interrupt playback early
//   - `getBlob()` to fetch the underlying audio Blob once available
//     (used by Voice Mode to attach a download button to the reply)
export function speakText(text, { language, speaker, onBlob } = {}) {
  let audioEl = null;
  let objectUrl = null;
  let audioBlob = null;
  let aborted = false;

  const promise = (async () => {
    if (!text || !text.trim()) return;
    const res = await fetch(`${API}/tts`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        text: text.slice(0, 2500),
        ...(language ? { language } : {}),
        ...(speaker ? { speaker } : {}),
      }),
    });
    if (!res.ok) {
      let msg = `Voice reply failed (${res.status})`;
      try {
        const j = await res.json();
        if (j?.detail) msg = j.detail;
      } catch {
        /* ignore */
      }
      throw new Error(msg);
    }
    if (aborted) return;
    const buf = await res.arrayBuffer();
    if (aborted) return;
    // Keep the blob alive after playback ends so the caller can offer a
    // download button. The garbage collector will reclaim it when the
    // message it's attached to falls out of scope.
    audioBlob = new Blob([buf], { type: "audio/mpeg" });
    if (onBlob) {
      try { onBlob(audioBlob); } catch { /* noop */ }
    }
    objectUrl = URL.createObjectURL(audioBlob);
    audioEl = new Audio(objectUrl);
    audioEl.playsInline = true;
    await new Promise((resolve, reject) => {
      audioEl.onended = resolve;
      audioEl.onerror = () => reject(new Error("Audio playback failed"));
      audioEl.play().catch(reject);
    });
  })().finally(() => {
    // Revoke only the playback URL — the Blob itself stays live for
    // download. The download button creates its own object URL and
    // revokes it right after the click.
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  });

  return {
    promise,
    getBlob: () => audioBlob,
    abort: () => {
      aborted = true;
      if (audioEl) {
        try {
          audioEl.pause();
          audioEl.currentTime = 0;
        } catch {
          /* ignore */
        }
      }
    },
  };
}

// --- Downloads --------------------------------------------------------
// Small helpers used by Voice Mode + Chat to save audio blobs and
// transcripts to disk. Kept in one place so the UX (filename shape,
// safe-chars, timestamp) stays consistent across screens.

const _EXT_BY_MIME = {
  "audio/mpeg": "mp3",
  "audio/mp3": "mp3",
  "audio/wav": "wav",
  "audio/x-wav": "wav",
  "audio/webm": "webm",
  "audio/ogg": "ogg",
  "audio/mp4": "m4a",
};

// Deterministic filename slug: kebab-case, ASCII-safe, ≤ 40 chars.
export function slugify(input, fallback = "chat") {
  const s = (input || "")
    .toLowerCase()
    .replace(/[^\w\s-]+/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 40);
  return s || fallback;
}

function _timestamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

// Force the browser to save a Blob via a temporary <a download> click.
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on the next tick — Safari sometimes needs the URL to still
  // be valid a hair after the click event.
  setTimeout(() => URL.revokeObjectURL(url), 250);
}

// Download an audio blob using its native MIME to pick the extension.
// Filename shape: `hbm-{role}-{slug}-{timestamp}.{ext}`.
export function downloadAudio(blob, { role = "audio", label = "" } = {}) {
  if (!blob) return;
  // MIME can carry codec params like `audio/webm;codecs=opus`, which
  // won't match the dict directly. Strip params, then also honour a
  // substring match so mp4 / m4a / webm / ogg all pick sensible
  // extensions.
  const rawType = (blob.type || "").toLowerCase();
  const bareType = rawType.split(";")[0].trim();
  let ext = _EXT_BY_MIME[bareType];
  if (!ext) {
    if (rawType.includes("webm")) ext = "webm";
    else if (rawType.includes("ogg")) ext = "ogg";
    else if (rawType.includes("mp4") || rawType.includes("m4a") || rawType.includes("aac")) ext = "m4a";
    else if (rawType.includes("wav")) ext = "wav";
    else if (rawType.includes("mpeg") || rawType.includes("mp3")) ext = "mp3";
    else ext = "audio";
  }
  const name = `hbm-${role}-${slugify(label, role)}-${_timestamp()}.${ext}`;
  downloadBlob(blob, name);
}

// Turn an array of {role, content} messages into a plain-text transcript.
// Format: `[HH:MM] You:` / `[HH:MM] HBM:` with a blank line between turns
// so it reads cleanly in any text editor.
export function buildTranscript(messages, { title, header } = {}) {
  const now = new Date();
  const stamp = now.toLocaleString();
  const lines = [];
  lines.push(header || "Human Behaviour AI — Conversation");
  if (title) lines.push(title);
  lines.push(`Exported ${stamp}`);
  lines.push("—".repeat(48));
  lines.push("");
  for (const m of messages || []) {
    if (!m || !m.content) continue;
    const who = m.role === "user" ? "You" : m.role === "assistant" ? "HBM" : (m.role || "note").toUpperCase();
    lines.push(`${who}:`);
    lines.push(String(m.content).trim());
    lines.push("");
  }
  return lines.join("\n");
}

// Save a transcript to disk as .txt. Title becomes part of the filename
// so users can find the right file in Downloads later.
export function downloadTranscript(messages, { title = "" } = {}) {
  const text = buildTranscript(messages, { title });
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const name = `hbm-transcript-${slugify(title, "session")}-${_timestamp()}.txt`;
  downloadBlob(blob, name);
}
